import { t } from 'i18next'
import { Outlet } from 'react-router'

import { useAtendimento } from '@/features/atendimento/hooks'
import { useAtendimentoAtivo } from '@/features/atendimento/hooks/use-atendimento-ativo'
import { RegionalSwitcher } from '@/features/regionais/components/regional-switcher'
import { useAuth } from '@/features/user/hooks'
import { DestructiveActionGuard } from '@/shared/components/destructive-action-guard'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import { SidebarProvider } from '@/shared/components/ui'
import { NavMain, type NavItem } from '@/shared/components/ui/sidebar/nav-main'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenuButton,
  SidebarRail,
} from '@/shared/components/ui/sidebar/sidebar'

const navMainItems: NavItem[] = [
  {
    title: t('layouts.sidebar.empreendimentos'),
    url: '/empreendimentos',
    icon: 'building-2',
  },
  {
    title: t('layouts.sidebar.espelho_de_vendas'),
    url: '/espelho-de-vendas',
    icon: 'floor-plan',
  },
  {
    title: t('layouts.sidebar.historico'),
    url: '/historico',
    icon: 'scroll-text',
    children: [
      { title: 'Simulações', url: '/historico/simulacoes' },
      { title: 'Propostas', url: '/historico/propostas' },
    ],
  },
]

const navMainAdminItems: NavItem[] = [
  {
    title: t('layouts.sidebar.configuracoes.titulo'),
    url: '/configuracoes',
    icon: 'settings',
    children: [
      { title: t('layouts.sidebar.configuracoes.cores_lotes'), url: '/configuracoes/cores-lotes' },
      { title: t('layouts.sidebar.configuracoes.parametros'), url: '/configuracoes/parametros' },
      {
        title: t('layouts.sidebar.configuracoes.reservas'),
        url: '/configuracoes/reserva',
      },
      { title: t('layouts.sidebar.configuracoes.perfil'), url: '/configuracoes/perfil' },
      { title: t('layouts.sidebar.configuracoes.termos'), url: '/configuracoes/termos' },
    ],
  },
]

export const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { user, logout } = useAuth()
  const { atendimento } = useAtendimento()
  useAtendimentoAtivo(!!user?.unidadeDeNegocioId)

  return (
    <SidebarProvider>
      <Sidebar data-testid="app-sidebar" collapsible="icon" {...props}>
        <SidebarHeader>
          <Render.If condition={user}>
            {(_user) => <RegionalSwitcher user={_user} isEmAtendimento={Boolean(atendimento?.id)} />}
          </Render.If>
        </SidebarHeader>
        <SidebarContent>
          <NavMain
            items={[
              ...navMainItems,
              ...(user?.perfilDoUsuario === 'AdministradorDaPlataforma' ? navMainAdminItems : []),
            ]}
          />
        </SidebarContent>
        <SidebarFooter>
          <DestructiveActionGuard
            title={t('login:dialog.logout.title')}
            description={t('login:dialog.logout.description')}
          >
            <SidebarMenuButton className="text-destructive" tooltip={t('layouts.sidebar.sair')} onClick={logout}>
              <Icon name="logout" />
              {t('layouts.sidebar.sair')}
            </SidebarMenuButton>
          </DestructiveActionGuard>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  )
}
